import React, { useMemo, useState } from 'react';

export const TAP = 44; // minimum touch target (px)
export const classLabel = (c) => (/^\d+$/.test(c) ? `Class ${c}` : c);

// One collapsible class row: search -> tap -> "Marked Absent" chips. Everyone else stays present.
export default function ClassAccordion({ cls, open, onToggle, absentIds, onAdd, onRemove }) {
  const [q, setQ] = useState('');
  const absentSet = useMemo(() => new Set(absentIds), [absentIds]);
  const byId = useMemo(() => Object.fromEntries(cls.students.map((s) => [s._id, s])), [cls.students]);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return cls.students
      .filter((s) => !absentSet.has(s._id) && ((s.name || '').toLowerCase().includes(t) || String(s.rollNumber || '') === t))
      .slice(0, 6);
  }, [q, cls.students, absentSet]);

  const present = cls.students.length - absentIds.length;

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', marginBottom: 10, overflow: 'hidden' }}>
      <button onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', minHeight: 60, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, border: 'none', background: open ? '#eff6ff' : '#fff', textAlign: 'left', cursor: 'pointer', fontSize: 16 }}>
        <span style={{ fontWeight: 800, flex: 1 }}>{classLabel(cls.classId)}</span>
        {cls.holiday
          ? <span style={{ fontSize: 12, background: '#fef3c7', padding: '4px 8px', borderRadius: 8 }}>🏖 {cls.holiday}</span>
          : <span style={{ fontSize: 13 }}>
              <b style={{ color: '#16a34a' }}>{present}</b> P · <b style={{ color: absentIds.length ? '#dc2626' : '#6b7280' }}>{absentIds.length}</b> A
            </span>}
        <span style={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>⌄</span>
      </button>

      {open && (
        <div style={{ padding: 14, borderTop: '1px solid #e5e7eb' }}>
          {cls.holiday ? (
            <div style={{ fontSize: 14, color: '#6b7280' }}>No attendance needed: {cls.holiday}.</div>
          ) : (
            <>
              <input type="search" inputMode="search" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={`Type a name or roll no. (${cls.students.length} students, all present)`}
                style={{ width: '100%', boxSizing: 'border-box', minHeight: TAP, padding: '0 12px', fontSize: 16, borderRadius: 10, border: '1.5px solid #d1d5db' }} />

              {matches.length > 0 && (
                <div style={{ marginTop: 6, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  {matches.map((s) => (
                    <button key={s._id} onClick={() => { onAdd(s._id); setQ(''); }}
                      style={{ width: '100%', minHeight: TAP, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 12px', border: 'none', borderBottom: '1px solid #f3f4f6', background: '#fff', fontSize: 15, cursor: 'pointer', textAlign: 'left' }}>
                      <span>{s.name}</span>
                      <span style={{ color: '#6b7280', fontSize: 12 }}>{s.rollNumber ? `Roll ${s.rollNumber} · ` : ''}tap = absent</span>
                    </button>
                  ))}
                </div>
              )}
              {q.trim() && matches.length === 0 && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>No matching present student.</div>}

              <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', margin: '12px 0 6px' }}>Marked Absent ({absentIds.length})</div>
              {absentIds.length === 0
                ? <div style={{ fontSize: 13, color: '#16a34a' }}>✅ Everyone present</div>
                : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {absentIds.map((id) => (
                      <button key={id} onClick={() => onRemove(id)} aria-label={`Remove ${byId[id]?.name} from absent`}
                        style={{ minHeight: TAP, padding: '0 12px', borderRadius: 22, border: 'none', background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                        {byId[id]?.name || id} ✕
                      </button>
                    ))}
                  </div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
